import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { generateTheme } from '@mercedes-benz/mbui-theme';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { toggleTheme as toggleThemeAction } from '../store/slices/themeSlice';

type TypographyVariant = 'default' | 'advertisment';

interface ThemeContextType {
  isDark: boolean;
  typographyVariant: TypographyVariant;
  toggleTheme: () => void;
  toggleTypography: () => void;
}

const ThemeContext = createContext<ThemeContextType>({
  isDark: true,
  typographyVariant: 'default',
  toggleTheme: () => {},
  toggleTypography: () => {},
});

export const useThemeContext = () => useContext(ThemeContext);

export const ThemeContextProvider = ({ children }: { children: ReactNode }) => {
  const dispatch = useAppDispatch();
  const variant = useAppSelector((s) => s.theme.variant);
  const [typographyVariant, setTypographyVariant] = useState<TypographyVariant>('default');

  const toggleTheme = () => dispatch(toggleThemeAction());
  const toggleTypography = () =>
    setTypographyVariant((prev) => (prev === 'default' ? 'advertisment' : 'default'));

  const theme = useMemo(
    () => generateTheme({ variant, typographyVariant }),
    [variant, typographyVariant]
  );

  useEffect(() => {
    // Keep a global theme marker so all CSS modules and portals resolve the same palette.
    document.documentElement.setAttribute('data-theme', variant);
    document.body.setAttribute('data-theme', variant);
    document.body.style.colorScheme = variant;
  }, [variant]);

  return (
    <ThemeContext.Provider value={{ isDark: variant === 'dark', typographyVariant, toggleTheme, toggleTypography }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
